export const TopDownItemId = {
    ChromeTV: 'chromeTV',
    Schedule: 'schedule',
    Matchbox: 'matchbox',
    Envelope: 'envelope',
    Shoes: 'shoes',
    Bowtie: 'bowtie',
    Lilly: 'lilly',
    RecordPlayer: 'recordPlayer',
    Hotels: 'postcard',
} as const;

export type TopDownItemId = typeof TopDownItemId[keyof typeof TopDownItemId];

export interface TopDownItemConfig {
    id: TopDownItemId;
    width: string;
    top: string;
    left: string;
    rotate: string;
    z: number;
    borderRadius?: string;
}

/**
 * Calculate distance from center point (50%, 50%)
 * @param top - Top position as percentage string
 * @param left - Left position as percentage string
 * @returns Distance from center
 */
function getDistanceFromCenter(top: string, left: string): number {
    const topNum = parseFloat(top);
    const leftNum = parseFloat(left);
    const centerTop = 50;
    const centerLeft = 50;
    return Math.sqrt(Math.pow(topNum - centerTop, 2) + Math.pow(leftNum - centerLeft, 2));
}

/**
 * Get configuration for top-down page items
 * Items use percentage-based widths relative to ContentContainer
 * Positions are arranged organically around center for portrait aspect ratio
 * @param mobile - Whether the device is mobile
 * @returns Configuration map keyed by item ID
 */
export function getTopDownItemsConfig(mobile: boolean): Map<TopDownItemId, TopDownItemConfig> {
    // Desktop positions - organic scattered layout
    const desktopItems: TopDownItemConfig[] = [
        {
            id: TopDownItemId.ChromeTV,
            width: '80%',
            top: '2.65%',
            left: '44.26%',
            rotate: '2deg',
            z: 0,
        },
        {
            id: TopDownItemId.Hotels,
            width: '44%',
            top: '68.94%',
            left: '8%',
            rotate: '-7deg',
            borderRadius: '3%',
            z: 0,
        },
        {
            id: TopDownItemId.RecordPlayer,
            width: '50%',
            top: '42.51%',
            left: '78.30%',
            rotate: '2deg',
            z: -1,
        },
        {
            id: TopDownItemId.Matchbox,
            width: '28%',
            top: '55%',
            left: '85%',
            rotate: '4deg',
            z: 1,
        },
        {
            id: TopDownItemId.Schedule,
            width: '70%',
            top: '65%',
            left: '44%',
            rotate: '5deg',
            z: 0,
        },
        {
            id: TopDownItemId.Envelope,
            width: '48%',
            top: '8%',
            left: '-5%',
            rotate: '-8deg',
            z: 0,
        },
        {
            id: TopDownItemId.Shoes,
            width: '45%',
            top: '49.21%',
            left: '-25%',
            rotate: '2deg',
            z: 10,
        },
        {
            id: TopDownItemId.Bowtie,
            width: '20%',
            top: '62%',
            left: '16%',
            rotate: '-6deg',
            z: 0,
        },
        {
            id: TopDownItemId.Lilly,
            width: '56%',
            top: '13.36%',
            left: '-10%',
            rotate: '6deg',
            z: 0,
        },
    ];

    // Mobile positions - organic scattered layout
    const mobileItems: TopDownItemConfig[] = [
        {
            id: TopDownItemId.ChromeTV,
            width: '80%',
            top: '0%',
            left: '30%',
            rotate: '3deg',
            z: 0,
        },
        {
            id: TopDownItemId.Bowtie,
            width: '20%',
            top: '63%',
            left: '22%',
            rotate: '-18deg',
            z: 1,
        },
        {
            id: TopDownItemId.RecordPlayer,
            width: '48%',
            top: '50.63%',
            left: '68.02%',
            rotate: '1deg',
            z: -1,
        },
        {
            id: TopDownItemId.Hotels,
            width: '44%',
            top: '68%',
            left: '4%',
            rotate: '-8deg',
            borderRadius: '3%',
            z: 0,
        },
        {
            id: TopDownItemId.Schedule,
            width: '64%',
            top: '68.46%',
            left: '40.27%',
            rotate: '4deg',
            z: 0,
        },
        {
            id: TopDownItemId.Lilly,
            width: '70%',
            top: '32.83%',
            left: '30.86%',
            rotate: '20deg',
            z: 0,
        },
        {
            id: TopDownItemId.Envelope,
            width: '40%',
            top: '10%',
            left: '2.25%',
            rotate: '-14deg',
            z: 0,
        },
        {
            id: TopDownItemId.Matchbox,
            width: '25%',
            top: '60%',
            left: '70%',
            rotate: '10deg',
            z: 0,
        },
        {
            id: TopDownItemId.Shoes,
            width: '40%',
            top: '42%',
            left: '-14%',
            rotate: '-2deg',
            z: 2,
        },
    ];

    const items = mobile ? mobileItems : desktopItems;

    // Convert array to Map keyed by item ID
    const itemsMap = new Map<TopDownItemId, TopDownItemConfig>();
    items.forEach((item) => {
        itemsMap.set(item.id, item);
    });

    return itemsMap;
}

